import { useLazyQuery } from '@apollo/client';
import React, { useContext, useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import { Redirect } from 'react-router-dom/cjs/react-router-dom.min';
import { AppContext } from '../../../../AppContext';
import { LIMIT, ORDER_STATUS, ORDER_TYPE, RETURN_ORDER_ITEM_STATUS, ROUTES, TABLE_FILTER_KEYS } from '../../../../common/constants';
import {
  FULFILMENT_POSSIBILITY_ORDERS_COUNT,
  ORDERS_COUNT,
} from '../all/graphql/Queries';
import AssignDeliveryPartner from './pages/AssignDeliveryPartner';
import FulfilmentPossibility from './pages/FulfilmentPossibility';
import New from './pages/New';
import Return from './pages/Return';
import { NEW_RETURN_ORDER_COUNT } from './graphql/Queries';
import useRetainedTableState from '../../../../hooks/useRetainedTableFilterState';

const NewWrapper = () => {
  const { dispatch, state: { orderCount } } = useContext(AppContext);
  const { retainedState } = useRetainedTableState();

  const [newOrdersAdminCountV2] = useLazyQuery(ORDERS_COUNT, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [newConfirmedOrdersAdminCountV2] = useLazyQuery(ORDERS_COUNT, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [fulfilmentPossibilityOrdersCount] = useLazyQuery(
    FULFILMENT_POSSIBILITY_ORDERS_COUNT,
    {
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  const [newReturnOrdersAdminCountV2] = useLazyQuery(
    NEW_RETURN_ORDER_COUNT,
    {
      onCompleted: (response) => {
        dispatch({
          type: 'SET_ORDER_COUNT',
          data: {
            ...orderCount,
            returnOrderCount: response?.returnOrders?.count,
            returnRequestedOrderCount:
              response?.returnOrders?.returnRequestedOrderCount,
          },
        });
      },
      fetchPolicy: 'network-only',
      onError() { },
    },
  );

  const getCountData = async () => {
    try {
      const retainedReturnFilters =
        retainedState?.[TABLE_FILTER_KEYS?.NEW_RETURN] || {};
      const [
        placedResponse,
        confirmedResponse,
        returnResponse,
        fulfilmentResponse,
      ] = await Promise.all([
        newOrdersAdminCountV2({
          variables: {
            filter: {
              orderType: ORDER_TYPE?.NEW,
              showPlacedOrderCount: true,
              status: [ORDER_STATUS?.PLACED?.value],
            },
          },
        }),
        newConfirmedOrdersAdminCountV2({
          variables: {
            filter: {
              orderType: ORDER_TYPE?.NEW,
              showConfirmedOrderCount: true,
              status: [
                ORDER_STATUS?.READY_TO_SHIP?.value,
                ORDER_STATUS?.CONFIRMED?.value,
              ],
            },
          },
        }),
        newReturnOrdersAdminCountV2({
          variables: {
            filter: {
              showReturnRequestedOrderCount: true,
              search: retainedReturnFilters?.searchTerm || '',
              limit: retainedReturnFilters?.paginationProp?.pageSize || LIMIT,
              skip:
                (retainedReturnFilters?.paginationProp?.current - 1) *
                (retainedReturnFilters?.paginationProp?.pageSize || 0),
              deliveryType: retainedReturnFilters?.filters?.deliveryType,
              dateRange: {
                from: retainedReturnFilters?.filters?.createdAt?.from?.$d,
                to: retainedReturnFilters?.filters?.createdAt?.to?.$d,
              },
              returnedOn: {
                from: retainedReturnFilters?.filters?.returnedOn?.from?.$d,
                to: retainedReturnFilters?.filters?.returnedOn?.to?.$d,
              },
              status:
                retainedReturnFilters?.filters?.status?.length > 0
                  ? retainedReturnFilters?.filters?.status
                  : [
                    RETURN_ORDER_ITEM_STATUS?.RETURN_REQUESTED?.value,
                    RETURN_ORDER_ITEM_STATUS?.READY_TO_PICKUP?.value,
                    RETURN_ORDER_ITEM_STATUS?.NOT_PICKED_UP?.value,
                  ],
              pincodeIds: retainedReturnFilters?.filters?.pincodes,
              deliveryPartnerIds:
                retainedReturnFilters?.filters?.deliveryPartnerName,
              returnOrderIds: retainedReturnFilters?.filters?.returnOrderNumber,
              toyIds: retainedReturnFilters?.filters?.productName,
              libraryIds: retainedReturnFilters?.filters?.name,
              receivingLibraryIds:
                retainedReturnFilters?.filters?.receivingLibName,
            },
          },
        }),
        fulfilmentPossibilityOrdersCount({
          variables: {
            filter: {
              orderType: ORDER_TYPE?.NEW,
              status: [ORDER_STATUS?.PLACED?.value],
            },
          },
          // Keep other tab counts working even if this query isn't ready yet.
        }).catch(() => ({})),
      ]);
      dispatch({
        type: 'SET_ORDER_COUNT',
        data: {
          newOrderCount: placedResponse?.data?.orders?.count ?? 0,
          placedOrderCount: placedResponse?.data?.orders?.count ?? 0,
          confirmedOrderCount: confirmedResponse?.data?.orders?.count ?? 0,
          remainingConfirmedOrderCount:
            confirmedResponse?.data?.orders?.confirmedOrderCount ?? 0,
          returnOrderCount: returnResponse?.data?.returnOrders?.count ?? 0,
          returnRequestedOrderCount:
            returnResponse?.data?.returnOrders
              ?.returnRequestedOrderCount ?? 0,
          fulfilmentPossibilityOrderCount:
            fulfilmentResponse?.data?.orders?.count ?? 0,
        },
      });
    } catch (error) {
      return error;
    }
  };

  useEffect(() => {
    getCountData();
  }, []);

  return (
    <Switch>
      <Route
        exact
        path={`${ROUTES?.ORDERS}${ROUTES?.NEW}`}
        render={() => (
          <Redirect
            to={`${ROUTES?.ORDERS}${ROUTES?.NEW}${ROUTES?.CONFIRM_ORDER}`}
          />
        )}
      />
      <Route
        exact
        path={`${ROUTES?.ORDERS}${ROUTES?.NEW}${ROUTES?.CONFIRM_ORDER}`}
        render={(props) => <New {...props} />}
      />
      <Route
        exact
        path={`${ROUTES?.ORDERS}${ROUTES?.NEW}${ROUTES?.FULFILMENT_POSSIBILITY}`}
        render={(props) => <FulfilmentPossibility {...props} />}
      />
      <Route
        exact
        path={`${ROUTES?.ORDERS}${ROUTES?.NEW}${ROUTES?.ASSIGN_DELIVERY_PARTNER}`}
        render={(props) => <AssignDeliveryPartner {...props} />}
      />
      <Route
        exact
        path={`${ROUTES?.ORDERS}${ROUTES?.NEW}${ROUTES?.RETURN}`}
        render={(props) => <Return {...props} />}
      />
    </Switch>
  );
};

export default NewWrapper;
